import { useOnClickOutside } from "@portal/hooks/useOnClickOutside";
import {
	useScrolledFromTopGestures,
	useScrolledFromTopReporter,
} from "@portal/providers/ScrolledFromTopReporterProvider";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { DataGridHeader } from "./DataGridHeader";
import { DataGridNavContext } from "./DataGridNavContext";
import { DataGridRowComponent } from "./DataGridRow";
import {
	DataGridStateProvider,
	useDataGridDispatch,
	useDataGridExpansionDispatch,
	useDataGridState,
} from "./state/DataGridStateProvider";
import {
	type DataGridColumnEntry,
	type DataGridGridLines,
	type DataGridLabelIndent,
	type DataGridRow,
	getGroupBoundaryColumnIds,
	getVisibleColumnDefs,
	gridLineClasses,
	isColumnGroup,
} from "./types";
import { buildNavigationMatrix } from "./utils/buildNavigationMatrix";

/** Set on the scroll region to the sticky header block's measured height and reset to 0 inside
 *  that block, so only scrolling cells clear the header and pinned rows when scrolled into view. */
const STICKY_BLOCK_OFFSET_VAR = "--datagrid-sticky-offset";

export type DataGridProps<TData = unknown> = {
	columns: DataGridColumnEntry<TData>[];
	rows: DataGridRow<TData>[];
	labelColumn: {
		label: string;
		width?: string;
		/** Depth-0 left padding of the sticky label cell. Defaults to "default" ("pl-4"). */
		indent?: DataGridLabelIndent;
		/** Drop the fixed-width spacer reserved for the expansion chevron on non-collapsible
		 *  rows. Use for flat grids (no expandable rows) to tighten the first column. */
		hideExpansionSpacer?: boolean;
		/** Custom node rendered in the first header cell in place of the label text. The `label`
		 *  remains the accessible fallback (e.g. used as the cell's `aria-label`). */
		renderHeader?: ReactNode;
		/** Classes applied to the first header cell when `renderHeader` is set. Defaults to
		 *  `pl-8 pr-4`. */
		headerClassName?: string;
		/** Custom node rendered in the label column's cell of the grouped sub-header row. Use when the
		 *  label column carries its own column headers that belong beside the leaf column labels. */
		renderSubHeader?: ReactNode;
	};
	toolbar?: ReactNode;
	/** Node rendered inside the grid's relative container (after the scroll region), within both
	 *  the DataGrid state and navigation providers. Use for full-height overlays (e.g. slide-over
	 *  drawers) that need `useDataGridExpansion*` and should span the grid via `absolute inset-y-0`. */
	overlay?: ReactNode;
	testId?: string;
	emptyMessage?: string;
	initialExpanded?: Iterable<string>;
	/** Horizontal scroll padding. The top offset comes from the measured sticky header block. */
	scrollPadding?: { left?: number; right?: number };
	/** Grid-line weight. "strong" darkens cell borders and adds an outer table border. */
	gridLines?: DataGridGridLines;
	/** Header row height in px. Lower it for flat, single-line-header grids; the default
	 *  is sized for two-line grouped headers (e.g. the financial statement grids). */
	headerHeight?: number;
	/** Minimum height (px) for non-compact rows. Defaults to 40; lower it for dense grids. */
	rowHeight?: number;
	/** Suppress the second (sub-label) header row that grouped grids render under each group.
	 *  Use when each group has a single, self-explanatory value column. */
	hideGroupSubHeader?: boolean;
	/** Cmd/Ctrl+E behavior. "toggle" (default) flips between fully expanded and fully collapsed.
	 *  "cycleDepth" steps through expansion one tree depth at a time, wrapping back to collapsed. */
	expandHotkeyMode?: "toggle" | "cycleDepth";
};

const collectExpandableIds = <TData,>(rows: DataGridRow<TData>[]): string[] => {
	const ids: string[] = [];
	const walk = (row: DataGridRow<TData>) => {
		const children = row.children ?? [];
		if (children.length === 0) return;
		if (!row.alwaysExpanded) ids.push(row.id);
		for (const child of children) walk(child);
	};
	for (const row of rows) walk(row);
	return ids;
};

/** Cumulative expansion steps for `cycleDepth` mode, one per tree depth. The leading `[]` is the
 *  fully-collapsed step the cmd+E cycle wraps back to; each later step adds the next depth's ids on
 *  top of all shallower ones. `alwaysExpanded` rows are skipped (they render children regardless).
 *  Returns `[]` when there are no toggleable rows. */
const collectExpandCycleSteps = <TData,>(rows: DataGridRow<TData>[]): string[][] => {
	const idsByDepth: string[][] = [];
	const walk = (row: DataGridRow<TData>, depth: number) => {
		const children = row.children ?? [];
		if (children.length === 0) return;
		if (!row.alwaysExpanded) (idsByDepth[depth] ??= []).push(row.id);
		for (const child of children) walk(child, depth + 1);
	};
	for (const row of rows) walk(row, 0);

	// `idsByDepth` is sparse: a depth with only leaf or `alwaysExpanded` rows leaves a hole, so skip
	// the empty slots when accumulating.
	const steps: string[][] = [[]];
	const cumulative: string[] = [];
	for (const ids of idsByDepth) {
		if (!ids) continue;
		cumulative.push(...ids);
		steps.push([...cumulative]);
	}
	return steps.length > 1 ? steps : [];
};

type DataGridInnerProps<TData> = Omit<DataGridProps<TData>, "initialExpanded">;

const DataGridInner = <TData = unknown>({
	columns,
	rows,
	labelColumn,
	toolbar,
	overlay,
	testId,
	emptyMessage,
	scrollPadding,
	gridLines = "default",
	headerHeight,
	rowHeight,
	hideGroupSubHeader = false,
	expandHotkeyMode = "toggle",
}: DataGridInnerProps<TData>) => {
	const state = useDataGridState();
	const dispatch = useDataGridDispatch();
	const { setExpanded } = useDataGridExpansionDispatch();
	const scrollRef = useRef<HTMLDivElement | null>(null);

	const expanded = state.expanded;
	const expandableIds = useMemo(() => collectExpandableIds(rows), [rows]);
	const expandCycleSteps = useMemo(
		() => (expandHotkeyMode === "cycleDepth" ? collectExpandCycleSteps(rows) : []),
		[rows, expandHotkeyMode],
	);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				dispatch({ type: "clear_active_cell" });
				return;
			}
			if (!(e.metaKey || e.ctrlKey) || e.key !== "e") return;
			const target = e.target as HTMLElement;
			if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;
			e.preventDefault();
			if (expandHotkeyMode === "cycleDepth" && expandCycleSteps.length > 0) {
				const currentIndex = expandCycleSteps.reduce(
					(deepest, ids, index) => (ids.every((id) => expanded.has(id)) ? index : deepest),
					0,
				);
				setExpanded(expandCycleSteps[(currentIndex + 1) % expandCycleSteps.length]);
				return;
			}
			const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));
			setExpanded(allExpanded ? [] : expandableIds);
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [expandableIds, expandCycleSteps, expandHotkeyMode, expanded, setExpanded, dispatch]);

	useOnClickOutside(scrollRef, () => dispatch({ type: "clear_active_cell" }));

	const visibleColumns = useMemo(() => getVisibleColumnDefs(columns), [columns]);
	const groupBoundaryColumnIds = useMemo(() => getGroupBoundaryColumnIds(columns), [columns]);
	// Grouped grids keep every group border; dropping the final one would leave the last group
	// trailing into the empty space that bounded columns can leave. Flat grids keep their flush edge.
	const hasGroups = useMemo(() => columns.some(isColumnGroup), [columns]);
	const lastColumnId = hasGroups ? undefined : visibleColumns[visibleColumns.length - 1]?.id;

	const labelWidth = labelColumn.width ?? "minmax(280px, 360px)";
	const gridTemplate = useMemo(
		() => `${labelWidth} ${visibleColumns.map((c) => c.width).join(" ")}`,
		[labelWidth, visibleColumns],
	);

	const pinnedRows = useMemo(() => rows.filter((row) => row.pinToTop), [rows]);
	const scrollingRows = useMemo(() => rows.filter((row) => !row.pinToTop), [rows]);

	const navigation = useMemo(() => {
		const pinnedMatrix = buildNavigationMatrix(pinnedRows, visibleColumns, expanded);
		const scrollingMatrix = buildNavigationMatrix(scrollingRows, visibleColumns, expanded);
		return {
			matrix: [...pinnedMatrix, ...scrollingMatrix],
			firstScrollingRowId: scrollingMatrix[0]?.[0]?.rowId,
			gridTemplate,
		};
	}, [pinnedRows, scrollingRows, visibleColumns, expanded, gridTemplate]);

	const [stickyBlockElement, setStickyBlockElement] = useState<HTMLDivElement | null>(null);
	useEffect(() => {
		const scrollElement = scrollRef.current;
		if (!stickyBlockElement || !scrollElement) return;
		const observer = new ResizeObserver(([entry]) => {
			const height = entry?.borderBoxSize?.[0]?.blockSize ?? entry?.contentRect.height ?? 0;
			scrollElement.style.setProperty(STICKY_BLOCK_OFFSET_VAR, `${Math.ceil(height)}px`);
		});
		observer.observe(stickyBlockElement);
		return () => {
			observer.disconnect();
			scrollElement.style.removeProperty(STICKY_BLOCK_OFFSET_VAR);
		};
	}, [stickyBlockElement]);

	const scrollStyle = useMemo(
		() => ({
			scrollPaddingLeft: scrollPadding?.left ? `${scrollPadding.left}px` : undefined,
			scrollPaddingRight: scrollPadding?.right ? `${scrollPadding.right}px` : undefined,
		}),
		[scrollPadding],
	);

	const { outerBorder } = gridLineClasses(gridLines);

	const renderRows = (list: DataGridRow<TData>[]) =>
		list.map((row) => (
			<DataGridRowComponent
				key={row.id}
				row={row}
				visibleColumns={visibleColumns}
				gridTemplate={gridTemplate}
				groupBoundaryColumnIds={groupBoundaryColumnIds}
				lastColumnId={lastColumnId}
				gridLines={gridLines}
				labelIndent={labelColumn.indent}
				hideExpansionSpacer={labelColumn.hideExpansionSpacer}
				rowHeight={rowHeight}
			/>
		));

	const scrolledFromTopReporting = useScrolledFromTopReporter();
	const { onScroll: handleScroll, attachNativeWheel } = useScrolledFromTopGestures(
		scrolledFromTopReporting?.reportScrolledFromTop ?? null,
		undefined,
		scrolledFromTopReporting?.getHeaderCollapsed,
	);

	const nativeWheelDisposerRef = useRef<(() => void) | undefined>(undefined);
	const setScrollRef = useCallback(
		(node: HTMLDivElement | null) => {
			scrollRef.current = node;
			scrolledFromTopReporting?.registerScrollElement(node);
			nativeWheelDisposerRef.current?.();
			nativeWheelDisposerRef.current = node ? attachNativeWheel(node) : undefined;
		},
		[scrolledFromTopReporting, attachNativeWheel],
	);

	return (
		<DataGridNavContext.Provider value={navigation}>
			<div className="relative flex-1 min-h-0 flex flex-col" data-testid={testId}>
				{toolbar}
				{rows.length === 0 && !labelColumn.renderHeader ? (
					<div className="px-4 py-8 text-tertiary text-sm">{emptyMessage}</div>
				) : (
					<div
						data-testid="data-grid-scroll-region"
						ref={setScrollRef}
						className={cn("flex-1 min-h-0 overflow-auto", outerBorder)}
						style={scrollStyle}
						onScroll={scrolledFromTopReporting ? handleScroll : undefined}
					>
						<div
							ref={setStickyBlockElement}
							data-testid="datagrid-pinned-header"
							className="sticky top-0 z-30 w-max min-w-full bg-surface-base [--datagrid-sticky-offset:0px]"
						>
							<DataGridHeader
								columns={columns}
								gridTemplate={gridTemplate}
								labelColumnLabel={labelColumn.label}
								labelColumnHeader={labelColumn.renderHeader}
								labelColumnHeaderClassName={labelColumn.headerClassName}
								labelColumnSubHeader={labelColumn.renderSubHeader}
								groupBoundaryColumnIds={groupBoundaryColumnIds}
								lastColumnId={lastColumnId}
								gridLines={gridLines}
								headerHeight={headerHeight}
								hideGroupSubHeader={hideGroupSubHeader}
							/>
							{renderRows(pinnedRows)}
						</div>

						{rows.length === 0 ? (
							<div className="px-4 py-8 text-tertiary text-sm">{emptyMessage}</div>
						) : (
							renderRows(scrollingRows)
						)}
					</div>
				)}
				{overlay}
			</div>
		</DataGridNavContext.Provider>
	);
};

export const DataGrid = <TData = unknown>({ initialExpanded, ...props }: DataGridProps<TData>) => (
	<DataGridStateProvider initialExpanded={initialExpanded}>
		<DataGridInner {...props} />
	</DataGridStateProvider>
);
